const IV_LENGTH = 12;
const TAG_LENGTH = 16;

/**
 * Decode a base64 string into a Uint8Array using browser-native atob.
 */
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Derive a 32-byte AES key from a shared secret using SHA-256.
 * Mirrors backend/utils/integrationCrypto.js deriveKeyFromSecret.
 */
async function deriveKey(secret) {
  const encoder = new TextEncoder();
  const data = encoder.encode(secret);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );
}

/**
 * Decrypt an integration token using AES-256-GCM.
 * Token format: base64(iv + tag + ciphertext)
 * Returns the parsed JSON payload object.
 */
export async function decryptIntegrationToken(token, secret) {
  if (!token || typeof token !== 'string') {
    throw new Error('Integration token is required');
  }
  if (!secret || typeof secret !== 'string') {
    throw new Error('Integration shared secret is required');
  }

  const combined = base64ToUint8Array(token);
  if (combined.length < IV_LENGTH + TAG_LENGTH) {
    throw new Error('Invalid integration token: too short');
  }

  const iv = combined.slice(0, IV_LENGTH);
  const tag = combined.slice(IV_LENGTH, IV_LENGTH + TAG_LENGTH);
  const ciphertext = combined.slice(IV_LENGTH + TAG_LENGTH);

  // Web Crypto AES-GCM expects ciphertext concatenated with tag
  const encrypted = new Uint8Array(ciphertext.length + tag.length);
  encrypted.set(ciphertext, 0);
  encrypted.set(tag, ciphertext.length);

  const key = await deriveKey(secret);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: TAG_LENGTH * 8 },
    key,
    encrypted
  );

  const decoder = new TextDecoder('utf-8');
  const json = decoder.decode(decrypted);
  return JSON.parse(json);
}

/**
 * Validate that all mandatory integration fields are present.
 *
 * `email` and `name` are optional: when a user comes from an external
 * application's login page they have not logged in yet, so there is no
 * name/email to prefill. In that case the payload is sent with empty
 * name/email and the user fills them manually on the ticket form.
 * Product/module/organization fields are always mandatory.
 */
export function validateIntegrationPayload(payload) {
  const required = [
    'product',
    'module',
    'organization_name',
    'parent_organization_name'
  ];
  const missing = required.filter((field) => !payload[field]);
  if (missing.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }
  return payload;
}
